import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Typography } from '@mui/material';
import { isBannerAsset, isCardAsset, isPostAsset, isVideoAsset } from 'common/type-guards';
import { CommonPageForm } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ProductId } from 'store/api/pages-api/pages-api-types';

import { ProductBannerForm, ProductPostForm, ProductsCardForm, ProductVideoForm } from './components';
import { styles } from './product-asset-subsection.styles';

interface Props {
  currentProductId: ProductId;
  productIdx: number;
}

interface SectionState {
  expanded: boolean;
  dialogOpen: boolean;
}

const SECTIONS = ['Videos', 'Banners', 'Features', 'Posts'] as const;

const ProductAssetSubsection = ({ currentProductId, productIdx }: Props) => {
  const { control } = useFormContext<CommonPageForm>();
  const fields = useFieldArray({ control, name: `products.${productIdx}.productAssets` });

  const videoCount = fields.fields.filter((f) => isVideoAsset(f) && f.type !== 'deleted').length;
  const bannerCount = fields.fields.filter((f) => isBannerAsset(f) && f.type !== 'deleted').length;
  const cardCount = fields.fields.filter((f) => isCardAsset(f) && f.type !== 'deleted').length;
  const postCount = fields.fields.filter((f) => isPostAsset(f) && f.type !== 'deleted').length;

  const [sectionStates, setSectionStates] = useState<Record<string, SectionState>>(
    Object.fromEntries(SECTIONS.map((s) => [s, { expanded: true, dialogOpen: false }])),
  );

  const handleAddClick = (label: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSectionStates((prev) => ({
      ...prev,
      [label]: { expanded: true, dialogOpen: true },
    }));
  };

  const handleClose = (label: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [label]: { ...prev[label], dialogOpen: false },
    }));
  };

  const handleToggle = (label: string) => {
    setSectionStates((prev) => ({
      ...prev,
      [label]: { ...prev[label], expanded: !prev[label].expanded },
    }));
  };

  const formProps = { assetFields: fields, currentProductId, productIdx };

  const sections = [
    { label: 'Videos', count: videoCount },
    { label: 'Banners', count: bannerCount },
    { label: 'Features', count: cardCount },
    { label: 'Posts', count: postCount },
  ];

  return (
    <Box sx={styles.wrapper}>
      {sections.map(({ count, label }) => {
        const state = sectionStates[label];
        return (
          <Accordion
            key={label}
            disableGutters
            expanded={state.expanded}
            onChange={() => handleToggle(label)}
            sx={styles.accordion}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
              <Box sx={styles.accordionHeader}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography fontWeight={600} variant="subtitle1">{label}</Typography>
                  {count > 0 && (
                    <Chip color="primary" label={count} size="small" variant="outlined" />
                  )}
                </Box>
                <Button
                  onClick={(e) => handleAddClick(label, e)}
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ mr: 1 }}
                  variant="outlined"
                >
                  Add
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionDetails}>
              {label === 'Videos' && (
                <ProductVideoForm
                  key={productIdx}
                  {...formProps}
                  onClose={() => handleClose(label)}
                  open={state.dialogOpen}
                />
              )}
              {label === 'Banners' && (
                <ProductBannerForm
                  key={productIdx}
                  {...formProps}
                  onClose={() => handleClose(label)}
                  open={state.dialogOpen}
                />
              )}
              {label === 'Features' && (
                <ProductsCardForm
                  key={productIdx}
                  {...formProps}
                  onClose={() => handleClose(label)}
                  open={state.dialogOpen}
                />
              )}
              {label === 'Posts' && (
                <ProductPostForm
                  key={productIdx}
                  {...formProps}
                  onClose={() => handleClose(label)}
                  open={state.dialogOpen}
                />
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export { ProductAssetSubsection };
